import React, { useEffect, useState } from "react";
import { DatePicker } from "antd";
import { ToastContainer, toast } from "react-toastify";
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import ExportButtons from "../../components/ExportButtons/ExportButtons";
import axios from "axios";
import { getHeaders } from "../../utility/getHeader";
import fetchData from "../../functions/fetchData";
import { fetchComboData } from "../../services/api";
import "react-toastify/dist/ReactToastify.css";
import "./EmployeeRecords.css";
import logo from "../../assets/logo.png";

const EmployeeRecords = () => {
  const [filterOptions, setFilterOptions] = useState({
    departments: [],
    positions: [],
    statuses: [
      { value: 'active', label: 'Active' },
      { value: 'probation', label: 'Probation' },
      { value: 'inactive', label: 'Inactive' }
    ]
  });

  const [filters, setFilters] = useState({
    department: "",
    position: "",
    status: "",
    search: ""
  });

  const [employeeData, setEmployeeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load filter options on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setLoading(true);
        
        // Fetch departments using fetchComboData
        const departmentsData = await fetchComboData('employee_departments', 'department_name');
        console.log('Departments data:', departmentsData);
        
        if (departmentsData && Array.isArray(departmentsData)) {
          const depts = departmentsData
            .filter(dept => dept.is_active)
            .map(dept => ({
              value: dept.id,
              label: dept.department_name
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          
          console.log('Processed departments:', depts);
          setFilterOptions(prev => ({
            ...prev,
            departments: depts
          }));
        }
        
        // Fetch positions using fetchComboData
        const positionsData = await fetchComboData('employee_positions', 'position_name');
        console.log('Positions data:', positionsData);
        
        if (positionsData && Array.isArray(positionsData)) {
          const positions = positionsData
            .filter(pos => pos.is_active)
            .map(pos => ({
              value: pos.id,
              label: pos.position_name,
              department_id: pos.department_id
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          
          console.log('Processed positions:', positions);
          setFilterOptions(prev => ({
            ...prev,
            positions: positions
          }));
        }
        
        setLoading(false);
        
      } catch (error) {
        console.error('Error loading filter options:', error);
        toast.error('Failed to load filter options.');
        setLoading(false);
      }
    };
    loadFilterOptions();
  }, []);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Load employee records
  const loadEmployees = async () => {
    try {
      setLoading(true);
      
      // Fetch all employees with error handling
      fetchData('employees', (employeesData) => {
        if (!employeesData || !Array.isArray(employeesData)) {
          toast.error('No employee data available');
          setLoading(false);
          return;
        }

        console.log('Employees data:', employeesData);
        
        // Apply filters
        let filtered = employeesData;
        
        if (filters.department) {
          filtered = filtered.filter(emp => emp.department_id === parseInt(filters.department));
        }
        
        if (filters.position) {
          filtered = filtered.filter(emp => emp.position_id === parseInt(filters.position));
        }
        
        if (filters.status) {
          filtered = filtered.filter(emp => emp.status?.toLowerCase() === filters.status.toLowerCase());
        }
        
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          filtered = filtered.filter(emp => 
            emp.employee_id?.toLowerCase().includes(searchLower) ||
            emp.english_first_name?.toLowerCase().includes(searchLower) ||
            emp.english_last_name?.toLowerCase().includes(searchLower) ||
            emp.thai_first_name?.toLowerCase().includes(searchLower) ||
            emp.thai_last_name?.toLowerCase().includes(searchLower) ||
            emp.phone_number?.toLowerCase().includes(searchLower) ||
            emp.email?.toLowerCase().includes(searchLower)
          );
        }
        
        // Enrich data with department and position names
        const enrichedData = filtered.map(emp => {
          const dept = filterOptions.departments.find(d => d.value === emp.department_id);
          const pos = filterOptions.positions.find(p => p.value === emp.position_id);
          
          return {
            ...emp,
            department_name: dept ? dept.label : emp.department || 'N/A',
            position_name: pos ? pos.label : emp.position || 'N/A'
          };
        });
        
        setEmployeeData(enrichedData);
        toast.success(`Loaded ${enrichedData.length} employee record${enrichedData.length !== 1 ? 's' : ''}`);
        setLoading(false);
      }, 'id', {});
      
    } catch (error) {
      console.error('Error loading employees:', error);
      toast.error('Error loading employee records. Please check if the employees table exists.');
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters({
      department: "",
      position: "",
      status: "",
      search: ""
    });
    setEmployeeData([]);
  };

  const getColumns = () => {
    return [
      { key: "employee_id", label: "Employee ID" },
      { key: "english_first_name", label: "First Name" },
      { key: "english_last_name", label: "Last Name" },
      { key: "department_name", label: "Department" },
      { key: "position_name", label: "Position" },
      { key: "phone_number", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "status", label: "Status" }
    ];
  };

  const getPDFExportColumns = () => {
    return [
      { key: "s_no", label: "S.No." },
      { key: "employee_id", label: "Employee ID" },
      { key: "employee_name", label: "Name" },
      { key: "position_name", label: "Position" },
      { key: "salary_amount", label: "Salary (THB)" }
    ];
  };

  const getExportColumns = () => {
    return [
      { key: "s_no", label: "S.No." },
      { key: "employee_id", label: "Employee ID" },
      { key: "employee_name", label: "Name" },
      { key: "department_name", label: "Department" },
      { key: "position_name", label: "Position" },
      { key: "salary_amount", label: "Salary (THB)" }
    ];
  };

  const toSentenceCase = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getExportData = () => {
    return employeeData.map((emp, idx) => ({
      s_no: idx + 1,
      ...emp,
      employee_name: toSentenceCase(`${emp.english_first_name || ''} ${emp.english_last_name || ''}`.trim()),
      salary_amount: emp.salary_amount ? parseFloat(emp.salary_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'
    }));
  };

  const formatValue = (value, key) => {
    if (!value && value !== 0) return "-";
    if (key === "status") {
      const statusColors = {
        active: 'success',
        probation: 'warning',
        inactive: 'secondary'
      };
      const color = statusColors[value?.toLowerCase()] || 'secondary';
      return <span className={`badge bg-${color}`}>{value}</span>;
    }
    return value;
  };

  const formatDateTime = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = days[currentDateTime.getDay()];
    const date = currentDateTime.toLocaleDateString('en-GB');
    const time = currentDateTime.toLocaleTimeString('en-US', { hour12: true });
    return { dayName, date, time };
  };

  const { dayName, date, time } = formatDateTime();

  // Get filtered positions based on selected department
  const getFilteredPositions = () => {
    if (!filters.department) {
      return filterOptions.positions;
    }
    return filterOptions.positions.filter(
      pos => pos.department_id === parseInt(filters.department)
    );
  };

  return (
    <Layout>
      <Header title="Employee Records" />
      <ToastContainer />
      <div className="employee-records-container">
        {/* Filter Card */}
        <div className="filter-card">
          <h5 style={{ marginTop: 0, marginBottom: 20, color: "#2c3e50", fontWeight: 600 }}>
            <i className="fa fa-filter"></i> Filter Criteria
          </h5>

          {/* Search Bar */}
          <div className="filter-section">
            <div className="filter-group" style={{ width: '100%' }}>
              <label>🔍 Search</label>
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search by ID, name, phone, or email..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #DAA520',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Filters */}
          <div className="filter-section">
            <div className="filter-group">
              <label>Department</label>
              <select name="department" value={filters.department} onChange={handleFilterChange}>
                <option value="">All Departments</option>
                {filterOptions.departments.map((dept, idx) => (
                  <option key={idx} value={dept.value}>{dept.label}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Position</label>
              <select 
                name="position" 
                value={filters.position} 
                onChange={handleFilterChange}
                disabled={!filters.department && getFilteredPositions().length === filterOptions.positions.length}
              >
                <option value="">All Positions</option>
                {getFilteredPositions().map((pos, idx) => (
                  <option key={idx} value={pos.value}>{pos.label}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Status</label>
              <select name="status" value={filters.status} onChange={handleFilterChange}>
                <option value="">All Status</option>
                {filterOptions.statuses.map((status, idx) => (
                  <option key={idx} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Action Bar */}
          <div className="action-bar">
            <button className="btn-modern btn-primary-modern" onClick={loadEmployees} disabled={loading}>
              <i className="fa fa-search"></i> Load Records
            </button>
            <button className="btn-modern btn-secondary-modern" onClick={resetFilters}>
              <i className="fa fa-refresh"></i> Reset
            </button>
            {employeeData.length > 0 && (
              <ExportButtons
                data={getExportData()}
                filename={`employee_records_${new Date().toISOString().split('T')[0]}`}
                title="Employee Records"
                columns={getExportColumns()}
                pdfColumns={getPDFExportColumns()}
                size="sm"
                logo={logo}
                orientation="portrait"
                headerInfo={{
                  dayName: dayName,
                  date: date,
                  time: time
                }}
              />
            )}
          </div>
        </div>

        {/* Data Card */}
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        ) : employeeData.length === 0 ? (
          <div className="data-card">
            <div className="empty-state">
              <div className="empty-state-icon">👥</div>
              <p>No employee records found. Select filters and click "Load Records"</p>
            </div>
          </div>
        ) : (
          <div className="data-card">
            <div className="data-card-header">
              <i className="fa fa-users"></i> Employee Records ({employeeData.length} employees)
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    {getColumns().map((col, idx) => (
                      <th key={idx}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employeeData.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {getColumns().map((col, colIdx) => (
                        <td key={colIdx}>{formatValue(row[col.key], col.key)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary Statistics */}
            <div className="summary-section">
              <div className="summary-card">
                <div className="summary-label">Total Employees</div>
                <div className="summary-value">{employeeData.length}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Active</div>
                <div className="summary-value text-success">
                  {employeeData.filter(e => e.status?.toLowerCase() === 'active').length}
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Probation</div>
                <div className="summary-value text-warning">
                  {employeeData.filter(e => e.status?.toLowerCase() === 'probation').length}
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Inactive</div>
                <div className="summary-value text-secondary">
                  {employeeData.filter(e => e.status?.toLowerCase() === 'inactive').length}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default EmployeeRecords;
